import React from "react";
import PropTypes from "prop-types";
import { Typography, Box } from "@material-ui/core";
import { Inp, Sel, Toggle, Btn } from "./UI";
import { AVAILABILITY_OPTIONS, AVAILABILITY_LABELS } from "../constants";
import { useServiceFormHook } from "../hooks/useServiceFormHook";
import { useStyles } from "./forms.style";

export const ServiceForm = ({ data, onChange, servicesRef, displayNameWhenNoRefs, hideIdentitySelector }) => {
  const classes = useStyles();
  const hasRefs = servicesRef?.length > 0;
  const serviceOptions = hasRefs
    ? servicesRef.map((svc) => ({ value: String(svc.id), label: svc.displayName ?? svc.name }))
    : [];
  const serviceValue = data.serviceId == null ? "" : String(data.serviceId);
  const showServiceField = !hideIdentitySelector;
  const { kvList, addKvRow, removeKvRow, updateKvRow } = useServiceFormHook(data, onChange);

  return (
    <Box>
      <Box className={classes.formGrid}>
        {showServiceField && hasRefs && (
          <Sel
            label="Service"
            value={serviceValue}
            onChange={(val) => onChange({ ...data, serviceId: val ? Number(val) : null })}
            options={serviceOptions}
            sx={{ marginBottom: 0 }}
          />
        )}
        {showServiceField && !hasRefs && displayNameWhenNoRefs && (
          <Box sx={{ marginBottom: 0 }}>
            <Typography className={classes.staticFieldLabel}>Service</Typography>
            <Typography className={classes.staticFieldValue}>{displayNameWhenNoRefs}</Typography>
          </Box>
        )}
        <Sel
          label="Visibility"
          value={data.availType}
          onChange={(val) => onChange({ ...data, availType: val })}
          options={AVAILABILITY_OPTIONS}
          sx={{ marginBottom: 0 }}
        />
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Toggle
            label="Active"
            checked={data.available}
            onChange={() => onChange({ ...data, available: !data.available })}
            sx={{ padding: "4px 0" }}
          />
        </Box>
        <Inp
          label="External ID"
          value={data.externalId ?? ""}
          onChange={(val) => onChange({ ...data, externalId: val || undefined })}
          placeholder="Optional"
          sx={{ marginBottom: 0 }}
        />
        <Inp
          label="Location-specific URL"
          value={data.locationSpcfcUrl ?? ""}
          onChange={(val) => onChange({ ...data, locationSpcfcUrl: val || undefined })}
          placeholder="Optional"
          sx={{ marginBottom: 0 }}
        />
      </Box>
      <Box className={classes.toggleRow}>
        <Toggle label="SSO Enabled" checked={data.sso} onChange={() => onChange({ ...data, sso: !data.sso })} sx={{ padding: "4px 0" }} />
        <Toggle
          label="Automated Provisioning"
          checked={data.autoProv}
          onChange={() => onChange({ ...data, autoProv: !data.autoProv })}
          sx={{ padding: "4px 0" }}
        />
        <Toggle
          label="Automated provisioning assn"
          checked={data.automatedProvisioningAssn ?? false}
          onChange={() => onChange({ ...data, automatedProvisioningAssn: !(data.automatedProvisioningAssn ?? false) })}
          sx={{ padding: "4px 0" }}
        />
      </Box>
      <Box className={classes.toggleRow}>
        <Toggle
          label="Assignable"
          checked={data.assignable}
          onChange={() => onChange({ ...data, assignable: !data.assignable })}
          sx={{ padding: "4px 0" }}
        />
        <Toggle
          label="Explorable"
          checked={data.explorable ?? false}
          onChange={() => onChange({ ...data, explorable: !(data.explorable ?? false) })}
          sx={{ padding: "4px 0" }}
        />
      </Box>
      <Box className={classes.kvSection}>
        <Typography className={classes.kvHelpText}>
          External group (optional) — Key-value pairs; value can be a string or JSON.
        </Typography>
        {kvList.map((row) => (
          <Box key={row.id} className={classes.kvRow}>
            <Inp
              label="Key"
              value={row.key}
              onChange={(val) => updateKvRow(row.id, "key", val)}
              placeholder="key"
              sx={{ marginBottom: 0 }}
            />
            <Inp
              label="Value"
              value={row.value}
              onChange={(val) => updateKvRow(row.id, "value", val)}
              placeholder="value or JSON"
              sx={{ marginBottom: 0 }}
            />
            <Btn variant="text" size="small" onClick={() => removeKvRow(row.id)} sx={{ minWidth: 0, marginTop: '8px' }}>
              Remove
            </Btn>
          </Box>
        ))}
        <Btn variant="outlined" size="small" onClick={addKvRow}>
          + Add entry
        </Btn>
      </Box>
    </Box>
  );
};

export const ResourceForm = ({ data, onChange, resourcesRef, displayNameWhenNoRefs, hideIdentitySelector }) => {
  const classes = useStyles();
  const hasRefs = resourcesRef?.length > 0;
  const resourceOptions = hasRefs
    ? resourcesRef.map((resource) => ({
        value: String(resource.id),
        label: resource.description ? `${resource.name} — ${resource.description}` : resource.name,
      }))
    : [];
  const resourceValue = data.resourceId == null ? "" : String(data.resourceId);
  const showResourceField = !hideIdentitySelector;

  return (
    <Box>
      {showResourceField && (
        <Box className={classes.compactGrid}>
          {hasRefs && (
            <Sel
              label="Resource"
              value={resourceValue}
              onChange={(val) => onChange({ ...data, resourceId: val ? Number(val) : null })}
              options={resourceOptions}
              sx={{ marginBottom: 0 }}
            />
          )}
          {!hasRefs && displayNameWhenNoRefs && (
            <Box sx={{ marginBottom: 0 }}>
              <Typography className={classes.staticFieldLabel}>Resource</Typography>
              <Typography className={classes.staticFieldValue}>{displayNameWhenNoRefs}</Typography>
            </Box>
          )}
          <Box />
        </Box>
      )}
      <Box className={classes.toggleRowNoMargin}>
        <Toggle
          label="Active"
          checked={data.active}
          onChange={() => onChange({ ...data, active: !data.active })}
          sx={{ padding: "4px 0" }}
        />
        <Toggle
          label="Auto-Assign (Open Access)"
          checked={data.autoAssign}
          onChange={() => onChange({ ...data, autoAssign: !data.autoAssign })}
          sx={{ padding: "4px 0" }}
        />
        <Toggle
          label="Hidden in Pathways"
          checked={data.hidden}
          onChange={() => onChange({ ...data, hidden: !data.hidden })}
          sx={{ padding: "4px 0" }}
        />
      </Box>
    </Box>
  );
};

export const ServiceSummary = ({ service }) => {
  const classes = useStyles();
  return (
    <Box className={classes.summaryRow}>
      <Typography component="span" variant="caption">{AVAILABILITY_LABELS[service.availType]}</Typography>
      <Typography component="span" variant="caption">{service.available ? "Active" : "Suspended"}</Typography>
      {service.sso && <Typography component="span" variant="caption">SSO</Typography>}
      {service.autoProv && <Typography component="span" variant="caption">Auto-Prov</Typography>}
      {service.assignable && <Typography component="span" variant="caption">Assignable</Typography>}
      {service.explorable && <Typography component="span" variant="caption">Explorable</Typography>}
      {service.automatedProvisioningAssn && <Typography component="span" variant="caption">Auto-Prov Assn</Typography>}
    </Box>
  );
};

export const ResourceSummary = ({ resource }) => {
  const classes = useStyles();
  return (
    <Box className={classes.summaryRow}>
      <Typography component="span" variant="caption">{resource.active ? "Active" : "Inactive"}</Typography>
      {resource.autoAssign && <Typography component="span" variant="caption">Auto-Assign</Typography>}
      {resource.hidden && <Typography component="span" variant="caption">Hidden</Typography>}
    </Box>
  );
};

const svcDataShape = PropTypes.shape({
  serviceId: PropTypes.number,
  availType: PropTypes.string,
  available: PropTypes.bool,
  active: PropTypes.bool,
  externalId: PropTypes.string,
  externalGroup: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  locationSpcfcUrl: PropTypes.string,
  sso: PropTypes.bool,
  autoProv: PropTypes.bool,
  automatedProvisioningAssn: PropTypes.bool,
  assignable: PropTypes.bool,
  explorable: PropTypes.bool,
});

const resDataShape = PropTypes.shape({
  resourceId: PropTypes.number,
  active: PropTypes.bool,
  autoAssign: PropTypes.bool,
  hidden: PropTypes.bool,
});

ServiceForm.propTypes = {
  data: svcDataShape.isRequired,
  onChange: PropTypes.func.isRequired,
  servicesRef: PropTypes.array,
  displayNameWhenNoRefs: PropTypes.string,
  hideIdentitySelector: PropTypes.bool,
};

ServiceForm.defaultProps = {
  servicesRef: [],
  displayNameWhenNoRefs: undefined,
  hideIdentitySelector: false,
};

ResourceForm.propTypes = {
  data: resDataShape.isRequired,
  onChange: PropTypes.func.isRequired,
  resourcesRef: PropTypes.array,
  displayNameWhenNoRefs: PropTypes.string,
  hideIdentitySelector: PropTypes.bool,
};

ResourceForm.defaultProps = {
  resourcesRef: [],
  displayNameWhenNoRefs: undefined,
  hideIdentitySelector: false,
};

ServiceSummary.propTypes = {
  service: PropTypes.shape({
    availType: PropTypes.string,
    available: PropTypes.bool,
    sso: PropTypes.bool,
    autoProv: PropTypes.bool,
    assignable: PropTypes.bool,
    explorable: PropTypes.bool,
    automatedProvisioningAssn: PropTypes.bool,
  }).isRequired,
};

ResourceSummary.propTypes = {
  resource: PropTypes.shape({
    active: PropTypes.bool,
    autoAssign: PropTypes.bool,
    hidden: PropTypes.bool,
  }).isRequired,
};
